// @flow
import React, { PureComponent } from 'react';
import Markdown from 'react-markdown';
import RemarkBreaks from 'remark-breaks';
import URI from 'urijs';
import classnames from 'classnames';

import { themes } from './Text.themes';

const DEFAULT_DOMAIN = 'clubmed';
const MARKDOWN_TEXT = [
  'break',
  'emphasis',
  'link',
  'list',
  'listItem',
  'paragraph',
  'strong',
  'heading',
];

export type TextProps = {
  className?: string,
  color?: 'current' | Colors,
  id?: string,
  domain?: string,
  hasLettrine?: boolean,
  hLevel?: number,
  text?: string,
  theme?: string,
  textTheme?: {
    p: string,
    span: string,
    ul: string,
    li: string,
  },
};

export class Text extends PureComponent<TextProps> {
  textTheme: {
    defaultColor: string,
    root: string,
    a: string,
    p: string,
    span: string,
    ul: string,
    li: string,
  };

  constructor(props: TextProps) {
    super(props);
    this.textTheme = themes[this.props.theme](this.props.color);
  }

  static defaultProps = {
    domain: DEFAULT_DOMAIN,
    hasLettrine: false,
    text: '',
    theme: 'text-sans-1',
  };

  markdownLinkRenderer = ({ children, href }: *) => {
    const { domain: defaultDomain } = this.props;
    const uri = new URI(href);
    const domain = uri.domain();

    if (domain && !domain.includes(defaultDomain)) {
      return (
        <a className={this.textTheme.a} href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    }

    return (
      <a className={this.textTheme.a} href={href}>
        {children}
      </a>
    );
  };

  markdownParagraphRenderer = ({ children }: *) => (
    <p className={classnames('last-child-mb-0', this.textTheme.p, this.props?.textTheme?.p)}>
      {children}
    </p>
  );

  markdownNeutralRenderer = ({ children }: *) => (
    <span
      className={classnames('last-child-mb-0', this.textTheme.span, this.props?.textTheme?.span)}
    >
      {children}
    </span>
  );

  markdownListRenderer = ({ children }: *) => (
    <ul className={classnames('last-child-mb-0', this.textTheme.ul, this.props?.textTheme?.ul)}>
      {children}
    </ul>
  );

  markdownListItemRenderer = ({ children }: *) => (
    <li className={classnames('last-child-mb-0', this.textTheme.li, this.props?.textTheme?.li)}>
      {children}
    </li>
  );

  markdownRootRenderer = (id: ?string, hLevel: number) => (props: *) => {
    const RootElementTag = hLevel ? `h${hLevel}` : 'div';
    return (
      <RootElementTag {...props} id={id}>
        {props.children}
      </RootElementTag>
    );
  };

  render() {
    const { className, color, hasLettrine, hLevel = 0, id, text } = this.props;
    const textColor = color || this.textTheme.defaultColor;

    if (!text) {
      return null;
    }

    return (
      <Markdown
        allowedTypes={MARKDOWN_TEXT}
        className={classnames(
          className,
          this.textTheme.root,
          { [`text-${textColor}`]: textColor },
          { 'text-lettrine': hasLettrine },
        )}
        escapeHtml
        plugins={[RemarkBreaks]}
        renderers={{
          root: this.markdownRootRenderer(id, hLevel),
          link: this.markdownLinkRenderer,
          paragraph: hLevel ? this.markdownNeutralRenderer : this.markdownParagraphRenderer,
          list: this.markdownListRenderer,
          listItem: this.markdownListItemRenderer,
        }}
        source={text}
      />
    );
  }
}
